/**
 * Reference clips for Seedance reference-to-video (Venice): which part of a
 * connected video is sent along, and the limits Venice enforces on it.
 *
 * The limits depend on the model family (https://docs.venice.ai/guides/media/seedance-2-0,
 * "Multimodal input limits"): Seedance 2.0 takes up to 3 clips of 2 to 15 seconds, 15 seconds
 * in total; Seedance 2.5 up to 10 clips of 2 to 30 seconds, 30 seconds in total. Either way
 * each clip is MP4 or MOV up to 50 MB. When a clip is used to extend a video, the generation
 * continues from the end of the selected part.
 */

export const CLIP_MIN_SEC = 2;
export const CLIP_MAX_BYTES = 50 * 1024 * 1024;

/** What a model takes as reference clips and reference audio. */
export interface ReferenceMediaLimits {
    maxClips: number;
    clipMaxSec: number;
    clipsTotalMaxSec: number;
    maxAudioClips: number;
    audioClipMaxSec: number;
    audioTotalMaxSec: number;
}

export const SEEDANCE_2_0_LIMITS: ReferenceMediaLimits = {
    maxClips: 3, clipMaxSec: 15, clipsTotalMaxSec: 15, maxAudioClips: 3, audioClipMaxSec: 15, audioTotalMaxSec: 15,
};
export const SEEDANCE_2_5_LIMITS: ReferenceMediaLimits = {
    maxClips: 10, clipMaxSec: 30, clipsTotalMaxSec: 30, maxAudioClips: 10, audioClipMaxSec: 30, audioTotalMaxSec: 30,
};
/** For models that do not say: the stricter family. */
export const DEFAULT_REFERENCE_LIMITS = SEEDANCE_2_0_LIMITS;

// The Seedance 2.0 values, for code that does not know the model
export const CLIP_MAX_SEC = SEEDANCE_2_0_LIMITS.clipMaxSec;
export const CLIPS_TOTAL_MAX_SEC = SEEDANCE_2_0_LIMITS.clipsTotalMaxSec;
export const CLIPS_MAX = SEEDANCE_2_0_LIMITS.maxClips;

export interface ClipRange {
    startSec: number;
    endSec: number;
}

const round1 = (value: number): number => Math.round(value * 10) / 10;

/** The part used by default: the whole clip, or its last part as long as the model takes when it is longer. */
export const defaultClipRange = (durationSec: number, limits: ReferenceMediaLimits = DEFAULT_REFERENCE_LIMITS): ClipRange => {
    const end = Math.max(0, durationSec);
    return { startSec: round1(Math.max(0, end - limits.clipMaxSec)), endSec: round1(end) };
};

/**
 * Keep a range inside the clip, never inverted, at least 2 s (or the whole clip
 * when shorter) and at most as long as the model takes. The edge that was moved wins;
 * the other edge follows when the limits require it.
 */
export const clampClipRange = (range: ClipRange, durationSec: number, moved: 'start' | 'end' = 'end', limits: ReferenceMediaLimits = DEFAULT_REFERENCE_LIMITS): ClipRange => {
    const maxLength = limits.clipMaxSec;
    const total = Math.max(0, durationSec);
    let start = Math.min(Math.max(0, range.startSec), total);
    let end = Math.min(Math.max(0, range.endSec), total);
    if (end < start) {
        if (moved === 'end') start = end;
        else end = start;
    }
    const minLength = Math.min(CLIP_MIN_SEC, total);
    if (end - start < minLength) {
        if (moved === 'end') start = Math.max(0, end - minLength);
        else end = Math.min(total, start + minLength);
        if (end - start < minLength) start = Math.max(0, end - minLength);
    }
    if (end - start > maxLength) {
        if (moved === 'end') start = end - maxLength;
        else end = start + maxLength;
    }
    return { startSec: round1(start), endSec: round1(end) };
};

export interface ClipSelection {
    label: string;
    startSec: number;
    endSec: number;
}

/** m:ss.t for timeline readouts. */
export const formatClock = (seconds: number): string => {
    const s = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
    const minutes = Math.floor(s / 60);
    const rest = s - minutes * 60;
    return `${minutes}:${rest < 10 ? '0' : ''}${rest.toFixed(1)}`;
};

/** Everything Venice would reject about these selections, in plain words. */
export const checkReferenceClips = (clips: ClipSelection[], limits: ReferenceMediaLimits = DEFAULT_REFERENCE_LIMITS): string[] => {
    const problems: string[] = [];
    if (clips.length > limits.maxClips) {
        problems.push(`This model takes at most ${limits.maxClips} reference clips; ${clips.length} are connected.`);
    }
    let total = 0;
    for (const clip of clips) {
        const length = clip.endSec - clip.startSec;
        total += length;
        if (length < CLIP_MIN_SEC - 0.05) {
            problems.push(`${clip.label}: the selection is ${formatClock(length)} long; each clip needs at least ${CLIP_MIN_SEC}s.`);
        } else if (length > limits.clipMaxSec + 0.05) {
            problems.push(`${clip.label}: the selection is ${formatClock(length)} long; each clip can be at most ${limits.clipMaxSec}s.`);
        }
    }
    if (total > limits.clipsTotalMaxSec + 0.05) {
        problems.push(`The selections add up to ${formatClock(total)}; all reference clips together can be at most ${limits.clipsTotalMaxSec}s. Shorten a selection or disconnect a clip.`);
    }
    return problems;
};
